import { expectedPlayerScore } from "../elo.js";
import type { EloRating, Question } from "../types.js";
import { normalize01, type GraphScores } from "./scores.js";
import type { SelectOptions } from "../selector.js";
import type { MasteryState } from "../mastery/masteryState.js";
import { effectivePlayerForConcept } from "../mastery/masteryState.js";

export type GraphSelectWeights = {
  /** Elo “fair match” (expected score near 0.5), default ~0.5 */
  elo: number;
  /** Emphasize high PageRank concepts (broad “hinge” topics), default ~0.25 */
  pageRank: number;
  /** Emphasize bridge nodes (betweenness), default ~0.15 */
  betweenness: number;
  /** Optional: earlier-in-topo first (0–1) or later (flip). Default 0. */
  topo?: number;
};

const DEFAULT_W: GraphSelectWeights = { elo: 0.5, pageRank: 0.3, betweenness: 0.15, topo: 0.05 };

function eloFitScore(playerRating: EloRating, qRating: EloRating): number {
  const e = expectedPlayerScore(playerRating, qRating);
  return 1 - 2 * Math.abs(e - 0.5);
}

function selectWithEffectiveRating(
  questions: readonly Question[],
  effectivePlayer: (q: Question) => EloRating,
  scores: GraphScores,
  options: SelectOptions & { weights?: Partial<GraphSelectWeights> } = {}
): Question | null {
  if (questions.length === 0) return null;
  const exclude = options.excludeIds ?? new Set();
  const w = { ...DEFAULT_W, ...options.weights };
  const startBand = options.preferredBand ?? 200;

  const allConcepts = [...new Set(questions.map((q) => q.conceptId ?? "foundations"))];
  const nPr = normalize01(scores.pageRank, allConcepts);
  const nBet = normalize01(scores.betweenness, allConcepts);
  const nTopo = scores.topoRank;

  const weightOne = (q: Question): number => {
    const c = q.conceptId ?? "foundations";
    const ep = effectivePlayer(q);
    const elo = Math.max(0, Math.min(1, eloFitScore(ep, q.rating)));
    const pr = nPr.get(c) ?? 0.5;
    const b = nBet.get(c) ?? 0.5;
    const tr = nTopo.get(c) ?? 0.5;
    const t = w.topo ?? 0.05;
    return w.elo * elo + w.pageRank * pr + w.betweenness * b + t * (1 - tr);
  };

  for (const band of bandSequence(startBand)) {
    const pool = questions.filter(
      (q) => !exclude.has(q.id) && Math.abs(q.rating - effectivePlayer(q)) <= band
    );
    if (pool.length > 0) {
      return pickByScore(pool, weightOne);
    }
  }
  const fallback = questions.filter((q) => !exclude.has(q.id));
  if (fallback.length > 0) {
    return pickByScore(fallback, weightOne);
  }
  return pickByScore([...questions], weightOne);
}

export function selectNextQuestionWithGraph(
  questions: readonly Question[],
  playerRating: EloRating,
  scores: GraphScores,
  options: SelectOptions & {
    weights?: Partial<GraphSelectWeights>;
  } = {}
): Question | null {
  return selectWithEffectiveRating(questions, () => playerRating, scores, options);
}

/**
 * Like `selectNextQuestionWithGraph`, but the Elo match and band use
 * `effectivePlayerForConcept(globalRating, q.conceptId, mastery, blend)` per question.
 */
export function selectNextQuestionWithGraphAndMastery(
  questions: readonly Question[],
  globalPlayerRating: EloRating,
  mastery: MasteryState,
  scores: GraphScores,
  options: SelectOptions & {
    weights?: Partial<GraphSelectWeights>;
    masteryBlend?: number;
  } = {}
): Question | null {
  const blend = options.masteryBlend ?? 0.45;
  return selectWithEffectiveRating(
    questions,
    (q) => effectivePlayerForConcept(globalPlayerRating, q.conceptId, mastery, blend),
    scores,
    options
  );
}

function pickByScore(pool: Question[], weight: (q: Question) => number): Question {
  return pool.reduce((best, q) => (weight(q) > weight(best) ? q : best));
}

function bandSequence(start: number): number[] {
  const steps: number[] = [start];
  let b = start;
  while (b < 2000) {
    b = Math.min(2000, Math.floor(b * 1.4) + 50);
    if (b > steps[steps.length - 1]!) steps.push(b);
  }
  if (steps[steps.length - 1]! < 2000) steps.push(2000);
  return steps;
}
